import { useEffect, useSyncExternalStore } from "react";

/**
 * Where toasts appear (v149.72).
 *
 * The app's toasts float at the top right (`components/AppToaster`). That
 * is fine on a screen with room to spare. The page designer has none: at
 * 320 × 568 or 844 × 390 every part of it is a control — Back, Add block,
 * Save & Publish, Page settings, the Edit / Preview tabs, block rows — so
 * a floating toast is always ON something, wherever it is put (top: the
 * header buttons and the picker's search; bottom: Page settings, the tabs,
 * the rows).
 *
 * So a screen can ask for a RESERVED lane instead (`useReservedToastLane`,
 * called by `pageDesigner/DesignerToastLane`): the toasts keep their look,
 * their corner and their stacking, but the screen makes room for them — a
 * band that is empty while they show — and tells the toaster where that
 * band is through `--toast-lane-top`. Nothing is underneath a toast.
 */
let holders = 0;
const listeners = new Set();
const emit = () => listeners.forEach((fn) => fn());
const subscribe = (fn) => {
  listeners.add(fn);
  return () => listeners.delete(fn);
};
const snapshot = () => (holders > 0 ? "reserved" : "floating");

/** "floating" (the app's default) or "reserved" (a screen made room). */
export function useToastLane() {
  return useSyncExternalStore(subscribe, snapshot, () => "floating");
}

/** Called by the component that keeps the lane's room (and its position). */
export function useReservedToastLane() {
  useEffect(() => {
    holders += 1;
    emit();
    return () => {
      holders -= 1;
      emit();
    };
  }, []);
}
